/**
 * @file TableWorkspaceModal.tsx
 * @description Primary workspace for table ordering, item staging, and delta kitchen dispatching.
 */

import React, { useState } from 'react';
import { X, Plus, Send, ArrowRightLeft, Users, Receipt, AlertCircle, Trash2, Ban } from 'lucide-react';
import { FastItemPickerModal } from '../../ordering/components/FastItemPickerModal';
import { MoveTableModal } from '../../tableactions/components/MoveTableModal';
import { TransferWaiterModal } from '../../tableactions/components/TransferWaiterModal';
import { CancelItemModal } from '../../modifications/components/CancelItemModal';
import { VoidOrderModal } from '../../tableactions/components/VoidOrderModal';
import { useOrderWorkspace } from '../../ordering/hooks/useOrderWorkspace';
import { moveTableOrder, transferTableWaiter, requestTableBill, cancelTableOrder } from '../../tableactions/services/tableAction.service';
import type { WaiterTable, StagedOrderItem } from '../../../types/waiter.types';

interface TableWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  table: WaiterTable;
  allTables: WaiterTable[];
  venueId: string;
  waiterId: string | null;
  waiterName: string;
  onTableUpdated: () => void;
}

export const TableWorkspaceModal: React.FC<TableWorkspaceModalProps> = ({
  isOpen,
  onClose,
  table,
  allTables,
  venueId,
  waiterId,
  waiterName,
  onTableUpdated,
}) => {
  const {
    categories,
    menuItems,
    stagedItems,
    subtotal,
    newItemsCount,
    isSending,
    addStagedItem,
    removeItem,
    sendToKitchen,
  } = useOrderWorkspace(venueId, table, waiterId, waiterName);

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isMoveOpen, setIsMoveOpen] = useState(false);
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [isVoidOpen, setIsVoidOpen] = useState(false);
  const [itemToCancel, setItemToCancel] = useState<StagedOrderItem | null>(null);
  const [billRequestedMessage, setBillRequestedMessage] = useState(false);

  if (!isOpen) return null;

  const handleSendToKitchen = async () => {
    try {
      await sendToKitchen();
      onTableUpdated();
    } catch (err) {
      console.error('Send to kitchen error:', err);
    }
  };

  const handleRequestBill = async () => {
    if (!table.active_order_id) return;
    try {
      await requestTableBill(table.active_order_id, venueId, table.id);
      setBillRequestedMessage(true);
      setTimeout(() => setBillRequestedMessage(false), 4000);
      onTableUpdated();
    } catch (err) {
      console.error('Bill request error:', err);
    }
  };

  const handleVoidConfirm = async (reason: string) => {
    if (!table.active_order_id) return;
    try {
      await cancelTableOrder(table.active_order_id, reason);
      onTableUpdated();
      onClose();
    } catch (err) {
      console.error('Void order error:', err);
    }
  };

  const handleMoveConfirm = async (destId: string, destNumber: string) => {
    if (!table.active_order_id) return;
    await moveTableOrder(table.active_order_id, destId, destNumber);
    onTableUpdated();
    onClose();
  };

  const handleTransferConfirm = async (newWaiterId: string, newWaiterName: string) => {
    if (!table.active_order_id) return;
    await transferTableWaiter(table.active_order_id, newWaiterId, newWaiterName);
    onTableUpdated();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-xl bg-zinc-950 border border-zinc-800 rounded-t-3xl sm:rounded-3xl h-[92dvh] sm:h-[85vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Mobile slide-up handle */}
        <div className="sm:hidden flex justify-center pt-2 pb-1 bg-zinc-900/60 shrink-0">
          <div className="w-10 h-1 rounded-full bg-zinc-700/60" />
        </div>

        {/* Table Workspace Header */}
        <div className="p-3.5 sm:p-4 border-b border-zinc-800 bg-zinc-900/60 flex items-center justify-between shrink-0">
          <div className="min-w-0 pr-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-black text-white tracking-tight leading-none">
                TABLE {table.table_number}
              </h2>
              <span className="text-xs text-zinc-400 bg-zinc-800 px-2 py-0.5 rounded-full font-medium truncate">
                {table.section} • {table.guest_count} guests
              </span>
            </div>
            <p className="text-xs text-zinc-400 mt-0.5 truncate">
              Server: <span className="text-zinc-200 font-semibold">{table.waiter_name || waiterName}</span>
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Bill Requested Alert Banner */}
        {billRequestedMessage && (
          <div className="p-3 bg-rose-950/80 border-b border-rose-800 text-rose-200 text-xs font-bold flex items-center gap-2">
            <Receipt className="w-4 h-4 text-rose-400" />
            <span>Bill requested successfully! Cashier & manager notified.</span>
          </div>
        )}

        {/* Action Toolbar */}
        <div className="p-2.5 sm:p-3 border-b border-zinc-800/80 bg-zinc-900/30 flex items-center gap-2 overflow-x-auto shrink-0 scrollbar-none">
          <button
            type="button"
            onClick={() => setIsPickerOpen(true)}
            className="min-h-[42px] px-3.5 py-2 rounded-xl bg-[#ff4f00] hover:bg-[#e64700] active:scale-[0.98] text-white text-xs font-black uppercase flex items-center gap-1.5 shadow-md shadow-[#ff4f00]/20 shrink-0 cursor-pointer transition-all"
          >
            <Plus className="w-4 h-4" />
            <span>Add Item</span>
          </button>

          <button
            type="button"
            disabled={!table.active_order_id}
            onClick={() => setIsMoveOpen(true)}
            className="min-h-[42px] px-3.5 py-2 rounded-xl bg-zinc-900 hover:bg-zinc-800 active:scale-[0.98] text-zinc-300 text-xs font-bold border border-zinc-800 flex items-center gap-1.5 shrink-0 disabled:opacity-40 cursor-pointer transition-all"
          >
            <ArrowRightLeft className="w-3.5 h-3.5" />
            <span>Move</span>
          </button>

          <button
            type="button"
            disabled={!table.active_order_id}
            onClick={() => setIsTransferOpen(true)}
            className="min-h-[42px] px-3.5 py-2 rounded-xl bg-zinc-900 hover:bg-zinc-800 active:scale-[0.98] text-zinc-300 text-xs font-bold border border-zinc-800 flex items-center gap-1.5 shrink-0 disabled:opacity-40 cursor-pointer transition-all"
          >
            <Users className="w-3.5 h-3.5" />
            <span>Transfer</span>
          </button>

          <button
            type="button"
            disabled={!table.active_order_id}
            onClick={handleRequestBill}
            className="min-h-[42px] px-3.5 py-2 rounded-xl bg-zinc-900 hover:bg-rose-950/40 active:scale-[0.98] text-rose-300 text-xs font-bold border border-zinc-800 hover:border-rose-800 flex items-center gap-1.5 shrink-0 disabled:opacity-40 cursor-pointer transition-all"
          >
            <Receipt className="w-3.5 h-3.5" />
            <span>Request Bill</span>
          </button>

          <button
            type="button"
            disabled={!table.active_order_id}
            onClick={() => setIsVoidOpen(true)}
            className="min-h-[42px] px-3.5 py-2 rounded-xl bg-zinc-900 hover:bg-rose-950/40 active:scale-[0.98] text-rose-400 text-xs font-bold border border-zinc-800 hover:border-rose-900 flex items-center gap-1.5 shrink-0 disabled:opacity-40 cursor-pointer transition-all"
          >
            <Ban className="w-3.5 h-3.5" />
            <span>Void Order</span>
          </button>
        </div>

        {/* Current Order Items */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-2">
          {stagedItems.length === 0 ? (
            <div className="h-64 flex flex-col items-center justify-center text-zinc-500 text-center px-4">
              <p className="text-sm font-semibold text-zinc-400">No items on Table {table.table_number}</p>
              <p className="text-xs text-zinc-600 mt-1">Tap "+ Add Item" to begin ordering</p>
            </div>
          ) : (
            stagedItems.map(item => (
              <div
                key={item.clientTempId}
                className={`p-3 rounded-2xl border transition-all ${
                  item.isNewStaged
                    ? 'bg-amber-500/5 border-amber-500/40 shadow-sm'
                    : item.status === 'cancelled'
                    ? 'opacity-40 line-through bg-zinc-900/40 border-zinc-800'
                    : 'bg-zinc-900/60 border-zinc-800/80'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2.5 min-w-0 flex-1">
                    <span className="w-6 h-6 rounded-md bg-zinc-800 text-zinc-200 font-mono font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">
                      {item.quantity}×
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-sm text-zinc-100 break-words">{item.name}</p>
                        {item.isNewStaged && (
                          <span className="text-[10px] font-black uppercase tracking-wider text-amber-400 bg-amber-500/20 px-1.5 py-0.2 rounded border border-amber-500/30 shrink-0">
                            NEW
                          </span>
                        )}
                      </div>
                      {item.cookingStyle && (
                        <p className="text-xs text-amber-400/90 font-medium mt-0.5">• {item.cookingStyle}</p>
                      )}
                      {item.modifiers.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {item.modifiers.map((m, idx) => {
                            const isRemoval = m.group_type === 'REMOVAL' || m.is_removal ||
                              /^(sans|no|without|pas de)\b/i.test(m.name.trim());
                            const qtyStr = m.quantity && m.quantity > 1 ? `${m.quantity}× ` : '';
                            return (
                              <span
                                key={idx}
                                className={`text-[11px] px-1.5 py-0.5 rounded border font-medium ${
                                  isRemoval
                                    ? 'bg-rose-950/60 text-rose-300 border-rose-800/80'
                                    : 'bg-zinc-800 text-zinc-300 border-zinc-700/60'
                                }`}
                              >
                                {isRemoval ? '✕ ' : '+ '}{qtyStr}{m.name}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      {item.notes && (
                        <p className="text-xs text-zinc-400 mt-0.5 italic">Note: {item.notes}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <span className="font-mono font-bold text-sm text-zinc-200 whitespace-nowrap">
                      {(item.price * item.quantity).toFixed(2)} {table.currency || 'AED'}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        if (item.isNewStaged) {
                          removeItem(item.clientTempId);
                        } else {
                          setItemToCancel(item);
                        }
                      }}
                      className="min-w-[40px] min-h-[40px] flex items-center justify-center rounded-xl bg-zinc-800/60 hover:bg-rose-950/40 text-zinc-400 hover:text-rose-400 transition-colors cursor-pointer"
                      title={item.isNewStaged ? 'Remove' : 'Cancel item with reason'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Subtotal & Delta Send Footer */}
        <div className="p-4 border-t border-zinc-800 bg-zinc-900/60 shrink-0 space-y-3 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:pb-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-zinc-400 font-medium">Subtotal</span>
            <span className="font-mono font-black text-xl text-white">
              {subtotal.toFixed(2)} {table.currency || 'AED'}
            </span>
          </div>

          <button
            type="button"
            disabled={newItemsCount === 0 || isSending}
            onClick={handleSendToKitchen}
            className={`w-full min-h-[52px] py-3.5 sm:py-4 rounded-2xl font-black text-sm tracking-wider uppercase flex items-center justify-center gap-2 shadow-lg transition-all ${
              newItemsCount > 0
                ? 'bg-[#ff4f00] hover:bg-[#e64700] text-white shadow-[#ff4f00]/25 active:scale-[0.98] cursor-pointer'
                : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
            }`}
          >
            <Send className="w-4 h-4 shrink-0" />
            <span>
              {isSending
                ? 'Sending to Kitchen...'
                : newItemsCount > 0
                ? `Send ${newItemsCount} New Item${newItemsCount > 1 ? 's' : ''} to Kitchen`
                : 'Kitchen Synchronized'}
            </span>
          </button>
        </div>
      </div>

      {/* Sub-Modals */}
      <FastItemPickerModal
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        categories={categories}
        menuItems={menuItems}
        currency={table.currency || 'AED'}
        onAddItem={(item, cooking, mods, notes, course) => {
          addStagedItem(item, cooking, mods, notes, course);
          setIsPickerOpen(false);
        }}
      />

      <MoveTableModal
        isOpen={isMoveOpen}
        onClose={() => setIsMoveOpen(false)}
        sourceTable={table}
        availableTables={allTables}
        onConfirmMove={handleMoveConfirm}
      />

      <TransferWaiterModal
        isOpen={isTransferOpen}
        onClose={() => setIsTransferOpen(false)}
        table={table}
        onConfirmTransfer={handleTransferConfirm}
      />

      {itemToCancel && (
        <CancelItemModal
          isOpen={true}
          onClose={() => setItemToCancel(null)}
          itemName={itemToCancel.name}
          onConfirmCancel={async reason => {
            await removeItem(itemToCancel.clientTempId, reason);
            setItemToCancel(null);
            onTableUpdated();
          }}
        />
      )}

      <VoidOrderModal
        isOpen={isVoidOpen}
        onClose={() => setIsVoidOpen(false)}
        orderNumber={table.active_order_number || undefined}
        tableNumber={table.table_number}
        onConfirmVoid={handleVoidConfirm}
      />
    </div>
  );
};
